<template>
  <div
    class="ai-file-block"
    :class="{ expired, loading }"
    role="button"
    tabindex="0"
    :title="expired ? 'Файл истёк или удалён' : (block.title || block.filename)"
    @click="download"
    @keydown.enter.prevent="download"
  >
    <span class="file-icon">{{ fileIcon }}</span>
    <span class="file-info">
      <span class="file-name">{{ block.filename }}</span>
      <span class="file-meta">
        {{ formatLabel }}<template v-if="block.rows != null"> · {{ block.rows }} строк</template> · {{ sizeLabel
        }}<template v-if="block.truncated"> · обрезан по лимиту</template>
      </span>
    </span>
    <span v-if="expired" class="file-expired">Файл истёк</span>
    <span v-else-if="loading" class="file-spinner"></span>
    <span v-else class="file-download">Скачать</span>
  </div>
</template>

<script>
import { useAxiosCreate } from '@/backend/utils';
import { filenameFromDisposition } from '@/backend/download';

const http = useAxiosCreate('api');

export default {
  name: 'AiFileBlock',
  props: {
    block: { type: Object, required: true },
  },
  data() {
    return { loading: false, expired: false };
  },
  computed: {
    fileIcon() {
      if (this.block.format === 'xlsx') return '📊';
      if (this.block.format === 'parquet') return '🗂️';
      return '📄';
    },
    formatLabel() {
      if (this.block.format === 'xlsx') return 'Excel';
      if (this.block.format === 'parquet') return 'Parquet';
      return 'CSV';
    },
    sizeLabel() {
      const bytes = Number(this.block.size_bytes) || 0;
      if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
      if (bytes >= 1024) return `${Math.round(bytes / 1024)} КБ`;
      return `${bytes} Б`;
    },
  },
  methods: {
    download() {
      if (this.loading || this.expired) return;
      const fileId = String(this.block.url || '').split('/').pop();
      if (!fileId) {
        this.expired = true;
        return;
      }
      this.loading = true;
      http
        .get(`ai/files/${fileId}`, { responseType: 'blob' })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/octet-stream',
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filenameFromDisposition(
            response.headers['content-disposition'],
            this.block.filename,
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          // 404: выгрузка протухла (TTL/рестарт пода) — повторных запросов не делаем
          this.expired = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.ai-file-block {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  color: inherit;
  max-width: 100%;
  transition: border-color 0.15s;
  cursor: pointer;
  user-select: none;
}
.ai-file-block:hover {
  border-color: #2563eb;
}
.ai-file-block.expired {
  cursor: default;
  opacity: 0.75;
}
.ai-file-block.expired:hover {
  border-color: #e5e7eb;
}
.file-icon {
  font-size: 22px;
  line-height: 1;
}
.file-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.file-name {
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-meta {
  font-size: 12px;
  color: #6b7280;
}
.file-download {
  margin-left: auto;
  font-size: 13px;
  color: #2563eb;
  white-space: nowrap;
}
.file-expired {
  margin-left: auto;
  font-size: 13px;
  color: #9ca3af;
  white-space: nowrap;
}
.file-spinner {
  margin-left: auto;
  width: 14px;
  height: 14px;
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: file-spin 0.8s linear infinite;
  flex-shrink: 0;
}
@keyframes file-spin {
  to { transform: rotate(360deg); }
}
</style>
